/**
 * Cooking Record — Prep List Upload Modal.
 *
 * Two-step flow inside a single modal:
 *   Step 1: pick facility / cook date / disposition / upload file
 *   Step 2: preview parsed rows, remap columns if auto-detect missed,
 *           trim bad rows, then commit → creates a `cook_prep_lists`
 *           doc with N pending items ready to be filled during service.
 *
 * The parse response uses fuzzy header matching on the backend so any
 * customer's Cook QA sheet layout works as long as it has an order#,
 * dish and cook-time column (in any order, with any punctuation).
 */
import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Upload, X, ChevronRight, ChevronLeft, Trash2, FileText, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";

const MAX_ITEMS = 500;

function todayISO() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

export default function PrepListUploadModal({ facilities, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [facilityId, setFacilityId] = useState("");
  const [cookDate, setCookDate] = useState(todayISO());
  const [disposition, setDisposition] = useState("served");
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);           // { headers, detected, rows, preview, ... }
  const [mapping, setMapping] = useState({ order_no: "", dish: "", cook_time: "" });
  const [rows, setRows] = useState([]);                    // editable rows for commit

  useEffect(() => {
    if (!facilityId && facilities.length === 1) setFacilityId(facilities[0].id);
  }, [facilities, facilityId]);

  const maxDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 10);
  }, []);

  // Step 1 → parse the uploaded file.
  const doParse = async () => {
    if (!facilityId) { toast.error("Pick a kitchen first."); return; }
    if (!cookDate) { toast.error("Pick a cook date."); return; }
    if (!file) { toast.error("Upload a .xlsx or .csv file."); return; }
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/hot-cold-hold/prep-lists/parse", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      setMapping({
        order_no: data.detected?.order_no || "",
        dish: data.detected?.dish || "",
        cook_time: data.detected?.cook_time || "",
      });
      // If auto-detect succeeded, use the pre-mapped preview rows; else empty.
      setRows((data.preview || []).map((r, idx) => ({ ...r, _key: `p${idx}` })));
      setStep(2);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Couldn't read the file.");
    } finally { setParsing(false); }
  };

  // When mapping changes, re-derive rows from `preview.rows` using the current mapping.
  const applyMapping = (m) => {
    if (!preview || !m.dish) return;
    const remapped = (preview.rows || [])
      .map((r, idx) => ({
        _key: `m${idx}`,
        order_no: String(r[m.order_no] || "").trim(),
        dish: String(r[m.dish] || "").trim(),
        cook_time: String(r[m.cook_time] || "").trim(),
      }))
      .filter((r) => r.dish);
    setRows(remapped);
  };

  const commit = async () => {
    if (!rows.length) { toast.error("No dishes to import."); return; }
    if (rows.length > MAX_ITEMS) { toast.error(`Max ${MAX_ITEMS} items per prep list.`); return; }
    // A prep list is site-scoped. Derive the site_id from the selected
    // facility so it works even when the top-bar shows "All sites"
    // (backend otherwise 400s with 'Pick a site before logging a record').
    const fac = facilities.find((f) => f.id === facilityId);
    if (!fac) { toast.error("Kitchen not found. Please re-select."); return; }
    setBusy(true);
    try {
      const fname = (file?.name || "").toLowerCase();
      const source = fname.endsWith(".csv") ? "csv"
        : fname.endsWith(".xlsx") ? "excel"
        : fname.endsWith(".pdf") ? "pdf"
        : "manual";
      const { data } = await api.post("/hot-cold-hold/prep-lists", {
        site_id: fac.site_id,
        facility_id: facilityId,
        cook_date: cookDate,
        disposition,
        source,
        file_name: file?.name || null,
        items: rows.map((r) => ({
          order_no: r.order_no || "",
          dish: r.dish,
          cook_time: r.cook_time || "",
        })),
      });
      toast.success(`Prep list created — ${data.prep_list.items.length} dishes ready to fill.`);
      onCreated(data.prep_list);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Couldn't create prep list.");
    } finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    // Trigger a signed download of the CSV template.
    api.get("/hot-cold-hold/prep-lists/template.csv", { responseType: "blob" })
      .then(({ data }) => {
        const url = URL.createObjectURL(data);
        const a = document.createElement("a");
        a.href = url;
        a.download = "cooking_prep_list_template.csv";
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Couldn't download template."));
  };

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.4)", zIndex: 12000 }}
      onClick={onClose} data-testid="prep-list-upload-modal">
      <div className="vb-card p-5"
        style={{ maxWidth: 780, width: "100%", maxHeight: "92vh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <h2 className="vb-heading text-xl m-0" style={{ color: "var(--vb-text-2)" }}>
              Upload Prep List
            </h2>
            <div className="text-[12px] opacity-70">
              Step {step} of 2 · {step === 1 ? "File & scope" : "Preview & commit"}
            </div>
          </div>
          <button onClick={onClose} className="vb-btn ghost sm" data-testid="prep-upload-close">
            <X size={16} />
          </button>
        </div>

        {step === 1 && (
          <div className="space-y-3">
            <div>
              <label className="text-[11px] opacity-70">Kitchen</label>
              <select className="vb-input" value={facilityId} onChange={(e) => setFacilityId(e.target.value)}
                data-testid="prep-upload-facility">
                <option value="">Select a kitchen…</option>
                {facilities.map((f) => (<option key={f.id} value={f.id}>{f.name}</option>))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] opacity-70">Cook date</label>
                <input type="date" className="vb-input" value={cookDate}
                  min={todayISO()} max={maxDate}
                  onChange={(e) => setCookDate(e.target.value)}
                  data-testid="prep-upload-date" />
              </div>
              <div>
                <label className="text-[11px] opacity-70">Disposition (locked for the whole list)</label>
                <select className="vb-input" value={disposition} onChange={(e) => setDisposition(e.target.value)}
                  data-testid="prep-upload-disposition">
                  <option value="served">Serve immediately</option>
                  <option value="cool_store">Cool &amp; store</option>
                </select>
              </div>
            </div>
            <div className="text-[11px] opacity-70">
              A prep list can't mix served &amp; cool-store dishes — upload two lists if you need both.
              Cook date can be up to 7 days ahead.
            </div>

            <div className="p-4 rounded" style={{ border: "1px dashed var(--vb-border)", background: "var(--vb-bg-2,#faf6f1)" }}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-[13px] font-semibold">Prep sheet file</div>
                <button className="vb-btn ghost sm" onClick={downloadTemplate} type="button"
                  data-testid="prep-upload-template">
                  <FileText size={14} /> Download template
                </button>
              </div>
              <input type="file" accept=".xlsx,.csv,.pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="prep-upload-file" />
              <div className="text-[11px] opacity-60 mt-1">
                Accepts .xlsx, .csv, or .pdf. Legacy .xls files aren't supported —
                save the file as .xlsx first. We auto-detect the Order #, Dish,
                and Cook Time columns; a mapping picker appears if any are missing.
                If we can't read the PDF, please export the sheet as .xlsx / .csv
                for the most reliable read.
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-2">
              <button className="vb-btn ghost" onClick={onClose} disabled={parsing}
                data-testid="prep-upload-cancel">Cancel</button>
              <button className="vb-btn" onClick={doParse} disabled={parsing || !file}
                data-testid="prep-upload-parse">
                {parsing ? "Reading…" : <>Next <ChevronRight size={14} /></>}
              </button>
            </div>
          </div>
        )}

        {step === 2 && preview && (
          <div className="space-y-3">
            {!Object.values(mapping).every(Boolean) && (
              <div className="p-3 rounded" style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.4)" }}>
                <div className="flex items-center gap-2 text-[12px]" style={{ color: "#991b1b" }}>
                  <AlertTriangle size={14} /> We couldn't auto-detect one or more columns. Pick them below.
                </div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3 text-[12px]">
              {["order_no", "dish", "cook_time"].map((k) => (
                <div key={k}>
                  <label className="text-[11px] opacity-70">
                    {k === "order_no" ? "Order #" : k === "dish" ? "Dish" : "Cook time"} column
                  </label>
                  <select className="vb-input" value={mapping[k]}
                    onChange={(e) => { const m = { ...mapping, [k]: e.target.value }; setMapping(m); applyMapping(m); }}
                    data-testid={`prep-upload-map-${k}`}>
                    <option value="">— pick column —</option>
                    {(preview.headers || []).map((h) => (<option key={h} value={h}>{h}</option>))}
                  </select>
                </div>
              ))}
            </div>

            <div className="text-[12px] opacity-70">
              {rows.length} row{rows.length === 1 ? "" : "s"} ready to import
              {preview.truncated && <> · file had more than {MAX_ITEMS} rows — truncated</>}.
            </div>

            <div className="vb-card overflow-auto" style={{ maxHeight: 340 }}>
              <table className="vb-table" data-testid="prep-upload-preview">
                <thead><tr><th style={{ width: 90 }}>Order #</th><th>Dish</th><th style={{ width: 120 }}>Cook time</th><th style={{ width: 40 }}></th></tr></thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={4} style={{ textAlign: "center", padding: 20 }} className="opacity-60">
                      No rows mapped yet. Pick columns above.
                    </td></tr>
                  )}
                  {rows.map((r, idx) => (
                    <tr key={r._key} data-testid={`prep-upload-row-${idx}`}>
                      <td>{r.order_no || <span className="opacity-40">—</span>}</td>
                      <td><strong>{r.dish}</strong></td>
                      <td>{r.cook_time || <span className="opacity-40">—</span>}</td>
                      <td>
                        <button className="vb-btn ghost sm" onClick={() => setRows((xs) => xs.filter((_, i) => i !== idx))}
                          data-testid={`prep-upload-row-remove-${idx}`}>
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between mt-2">
              <button className="vb-btn ghost" onClick={() => setStep(1)} disabled={busy}
                data-testid="prep-upload-back">
                <ChevronLeft size={14} /> Back
              </button>
              <button className="vb-btn" onClick={commit} disabled={busy || rows.length === 0}
                data-testid="prep-upload-commit">
                <Upload size={14} /> {busy ? "Creating…" : `Create prep list (${rows.length})`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
